import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isEqual } from "lodash";
import { useState } from "react";

import { ErrandTypeRelativeToVisitEnum } from "~/graphql/__generated__/graphql";
import { ErrandType } from "~/providers/OrgConfigProvider";

type Props = {
  errandType: ErrandType;
};

type EditableErrandType = Pick<ErrandType, "name" | "description" | "relativeToVisit" | "active">;

const ConfigureErrandType = ({ errandType }: Props) => {
  const base: EditableErrandType = {
    name: errandType.name,
    description: errandType.description,
    relativeToVisit: errandType.relativeToVisit,
    active: errandType.active,
  };
  const [local, setLocal] = useState(base);
  const [updateErrandType, { loading }] = useMutation(UPDATE_ERRAND_TYPE);

  const onSave = async () => {
    await updateErrandType({
      variables: { id: errandType.id, ...local },
      awaitRefetchQueries: true,
      refetchQueries: ["OrgConfig"],
    });
  };

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography sx={{ mb: 2 }}>Errand</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Name"
              value={local.name}
              onChange={event => setLocal({ ...local, name: event.target.value })}
              disabled={loading}
              required
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Relative to Visit"
              value={local.relativeToVisit}
              onChange={event => setLocal({
                ...local,
                relativeToVisit: event.target.value as ErrandTypeRelativeToVisitEnum,
              })}
              disabled={loading}
              select
              fullWidth
              size="small"
            >
              <MenuItem value={ErrandTypeRelativeToVisitEnum.Before}>Before</MenuItem>
              <MenuItem value={ErrandTypeRelativeToVisitEnum.After}>After</MenuItem>
            </TextField>
          </Grid>
          <Grid size={12}>
            <TextField
              label="Description"
              value={local.description}
              onChange={event => setLocal({ ...local, description: event.target.value })}
              disabled={loading}
              multiline
              minRows={2}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              label="Active"
              control={
                <Switch
                  checked={local.active}
                  onChange={event => setLocal({ ...local, active: event.target.checked })}
                  disabled={loading}
                />
              }
            />
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Button
          variant="contained"
          size="small"
          disabled={!local.name.trim() || isEqual(local, base)}
          loading={loading}
          onClick={onSave}
        >
          Save
        </Button>
        <Button size="small" variant="outlined" color="secondary" onClick={() => setLocal(base)}>
          Reset
        </Button>
      </CardActions>
    </Card>
  );
};

export default ConfigureErrandType;

const UPDATE_ERRAND_TYPE = gql(`
  mutation UpdateErrandType(
    $id: Int!
    $name: String!
    $description: String!
    $relativeToVisit: ErrandTypeRelativeToVisitEnum!
    $active: Boolean!
  )
  @permission(requires: manageOrgConfig)
  {
    updateErrandType(
      id: $id
      name: $name
      description: $description
      relativeToVisit: $relativeToVisit
      active: $active
    ) {
      id
    }
  }
`);
